import React, { createContext, useContext } from 'react';
import { useReduxAuth } from '@/hooks/useReduxAuth';
import type { AuthContextType } from '@/types/auth';

// Create Auth Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider Props
interface AuthProviderProps {
  children: React.ReactNode;
}

// Auth Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Use Redux auth hook instead of local state management
  const authContextValue = useReduxAuth();

  return <AuthContext.Provider value={authContextValue}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

// Hook to get current user
// eslint-disable-next-line react-refresh/only-export-components
export const useUser = () => {
  const { user, loading, isAuthenticated } = useAuth();
  return { user, loading, isAuthenticated };
};

// Hook for auth status
// eslint-disable-next-line react-refresh/only-export-components
export const useAuthStatus = () => {
  const { isAuthenticated, loading } = useAuth();
  return { isAuthenticated, loading };
};
